import type { SessionHint } from "@/lib/session-hint";
import type { LearnLessonAccessState } from "./api";

/**
 * Whether a locked lesson's answer is still on its way (`LN-RDR03`, founder
 * 2026-10-08).
 *
 * The reader's page is ISR-cached from the **anonymous** payload, so every
 * gated lesson arrives saying `sign_in` and the reader's own payload replaces
 * it a round trip later. Until then the reader used to draw the sign-in card,
 * and a signed-in learner saw "Sign in" blink for about a second on every
 * gated lesson they opened. "Not known yet" now draws a neutral frame instead,
 * and the sign-in card is kept for the two cases that are known: nobody is
 * signed in, or the reader's own payload still says the lesson is locked.
 *
 * - `isPlaceholderData`: the query is still showing the anonymous payload;
 *   once the reader's own answer is in (or in the cache from a prefetch) it is
 *   `false` and `access` is the real one.
 * - `sessionHint`: `"none"` means this browser has no session to restore, so a
 *   visitor who never signed in gets the sign-in card as soon as auth settles.
 * - A failed read falls back to the card rather than waiting forever.
 */
export function lessonAccessPending(input: {
  access: LearnLessonAccessState;
  isPlaceholderData: boolean;
  isError: boolean;
  isAuthenticated: boolean;
  authLoading: boolean;
  sessionHint: SessionHint | null;
}): boolean {
  if (input.access === "granted" || !input.isPlaceholderData || input.isError) return false;
  if (input.isAuthenticated) return true;
  return input.authLoading && input.sessionHint !== "none";
}

/**
 * The lesson a reader link points at, read off its href, so one handler on the
 * reader can prefetch for every lesson link it holds (the rail, the syllabus,
 * previous, next and the module test) without each of them knowing about it.
 */
export function lessonRefFromHref(
  href: string,
  trackSlug: string,
): { moduleKey: string; lessonKey: string } | null {
  let path: string;
  try {
    path = new URL(href, "http://x").pathname;
  } catch {
    return null;
  }
  const match = /^\/(?:en|bn)\/learn\/([^/]+)\/([^/]+)\/([^/]+)\/?$/.exec(path);
  if (!match || decodeURIComponent(match[1]) !== trackSlug) return null;
  return { moduleKey: decodeURIComponent(match[2]), lessonKey: decodeURIComponent(match[3]) };
}
